import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PENDING = ['queued', 'running']
const LIVE = ['starting', 'ready']

// The browser Rails console at /admin/rails_console. Like the logs page it has
// no TTL cache: the session lives in a worker process and is re-read on every
// visit. See docs/rails_console.md and
// app/controllers/api/v1/admin/console_sessions_controller.rb.
export const useAdminRailsConsoleStore = defineStore('adminRailsConsole', {
  state: () => ({
    session: null,
    commands: [],
    confirmWith: 'password', // 'two_factor' | 'password' | 'none'
    environment: '',
    railsVersion: '',
    appName: 'rails',
    liveCount: 0,
    maxLive: 3,
    loading: false,
    error: null,       // page level error (skeleton → error state)
    starting: false,
    startError: null,
    runError: null,
  }),

  getters: {
    live: (state) => !!state.session && LIVE.includes(state.session.status),
    ready: (state) => state.session?.status === 'ready',
    // A local placeholder counts as running too (input stays disabled), but
    // it cannot be interrupted until the server has given it an id.
    running: (state) => state.commands.find((command) => PENDING.includes(command.status)) || null,
  },

  actions: {
    async request(method, url, data = {}) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest(method, url, data, false)
      if (!res.js_error) return res.response
      // 4xx answers carry a message the page should show; axios rejects them.
      const body = res.error?.response?.data
      return body && typeof body === 'object' ? body : { success: false, message: 'Something went wrong.' }
    },

    async fetchCurrent() {
      this.loading = true
      this.error = null
      try {
        const res = await this.request('GET', '/api/v1/admin/console_sessions/current')
        if (!res.success) {
          this.error = res.message || 'Failed to load the console.'
          return
        }
        const data = res.data
        this.confirmWith = data.confirm_with
        this.environment = data.environment
        this.railsVersion = data.rails_version
        this.appName = data.app_name
        this.liveCount = data.live_count
        this.maxLive = data.max_live
        this.setSession(data.session)
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    setSession(session) {
      if (!session) {
        this.session = null
        this.commands = []
        return
      }
      const { commands, ...rest } = session
      this.session = rest
      if (commands) this.commands = commands
    },

    // { reason, writable, code, password } → true when the worker is starting.
    async start(params) {
      this.starting = true
      this.startError = null
      try {
        const res = await this.request('POST', '/api/v1/admin/console_sessions', params)
        if (!res.success) {
          this.startError = res.message || 'Could not start the console.'
          return false
        }
        this.setSession(res.data)
        return true
      } finally {
        this.starting = false
      }
    },

    // Re-reads the session: used while the worker boots and now and then
    // afterwards, so an idle timeout or a dead worker shows up on the page.
    async refreshSession() {
      if (!this.session) return
      const res = await this.request('GET', `/api/v1/admin/console_sessions/${this.session.id}`)
      if (res.success) this.session = { ...this.session, ...res.data }
    },

    // The command shows straight away as queued (a local placeholder), so the
    // code never disappears between Enter and the server's answer.
    async run(code) {
      this.runError = null
      const placeholder = {
        id: `local-${Date.now()}`,
        position: (this.commands.at(-1)?.position || 0) + 1,
        code,
        status: 'queued',
        queries: [],
        local: true,
      }
      this.commands.push(placeholder)
      const res = await this.request('POST', `/api/v1/admin/console_sessions/${this.session.id}/commands`, { code })
      const index = this.commands.findIndex((c) => c.id === placeholder.id)
      if (!res.success) {
        if (index >= 0) this.commands.splice(index, 1)
        this.runError = res.message || 'Could not run that.'
        await this.refreshSession()
        return null
      }
      if (index >= 0) this.commands.splice(index, 1, res.data)
      else this.commands.push(res.data)
      return res.data
    },

    // One poll of a pending command; returns true once it has finished.
    async pollCommand(id) {
      const res = await this.request('GET', `/api/v1/admin/console_commands/${id}`)
      if (!res.success) return false
      this.replaceCommand(res.data)
      return !PENDING.includes(res.data.status)
    },

    async interrupt(id) {
      const res = await this.request('POST', `/api/v1/admin/console_commands/${id}/interrupt`)
      if (res.success) this.replaceCommand(res.data)
    },

    replaceCommand(command) {
      const index = this.commands.findIndex((c) => c.id === command.id)
      if (index >= 0) this.commands.splice(index, 1, command)
    },

    // Suggestions for the code before the cursor, from the session's worker.
    // Returns { prefix, items } or null; never throws.
    async complete(text) {
      if (this.session?.status !== 'ready') return null
      const res = await this.request('POST', `/api/v1/admin/console_sessions/${this.session.id}/complete`, { text })
      return res.success ? res.data : null
    },

    async close() {
      if (!this.session) return
      const res = await this.request('POST', `/api/v1/admin/console_sessions/${this.session.id}/close`)
      if (res.success) this.session = { ...this.session, ...res.data }
    },
  },
})
